// AddProductPage – CSV or Manual option, premium UX
import { useState, useCallback, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { useDropzone } from 'react-dropzone';
import { FiUpload, FiX, FiArrowLeft, FiDownload, FiSave, FiAlertCircle, FiCheckCircle, FiZap, FiLock, FiCheck } from 'react-icons/fi';
import { BsFileSpreadsheet, BsFillKeyboardFill, BsFileEarmarkText, BsClipboardData, BsGrid3X3Gap, BsImages, BsSliders, BsBox } from 'react-icons/bs';
import { BiLoaderAlt } from 'react-icons/bi';
import { MdUploadFile, MdBarChart, MdAttachMoney, MdInventory } from 'react-icons/md';
import toast from 'react-hot-toast';
import * as XLSX from 'xlsx';
import ExcelJS from 'exceljs';
import Swal from 'sweetalert2';
import { productAPI } from '../../api';
import './Product.css';

import { CSVUploadPanel, TemplateSelectionModal, downloadTemplate } from './AddCsvProducts';
import ManualAddPanel from './AddManualProduct';

export default function AddProductPage() {
  const navigate = useNavigate();
  const [mode, setMode] = useState<'choose' | 'csv' | 'manual'>('choose');
  const [isTemplateModalOpen, setIsTemplateModalOpen] = useState(false);
  return (
    <div className="fade-in" style={{ display: 'flex', flexDirection: 'column', minHeight: 'calc(100vh - 80px)' }}>
      <div style={{ marginBottom: '24px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '12px' }}>
        <div>
          <button
            onClick={() => mode === 'choose' ? navigate('/products') : setMode('choose')}
            className="back-pill-btn"
          >
            <FiArrowLeft size={16} style={{ display: 'inline-block' }} />
            {mode === 'choose' ? 'Back to Products' : 'Change Method'}
          </button>
          <h1 className="page-title" style={{ fontSize: '24px', fontWeight: '800', color: '#0f172a', marginBottom: '4px' }}>
            {mode === 'choose' && 'Add Products'}
            {mode === 'manual' && 'Add Product Manually'}
          </h1>
          <p style={{ color: '#64748b', fontSize: '14px', margin: 0 }}>
            {mode === 'choose' && 'Choose a method to add products to your store'}
            {mode === 'manual' && 'Fill in the form details below to add a single product'}
          </p>
        </div>
      </div>

      {/* Method Selection Cards */}
      {mode === 'choose' && (
        <div style={{ maxWidth: '100%', padding: '0 4px', display: 'flex', flexDirection: 'column', gap: '14px', flex: 1 }}>

          {/* ── Hero Banner with Download Strip inside ── */}
          <div style={{
            background: 'linear-gradient(135deg, #0f172a 0%, #1e3a5f 55%, #0284c7 100%)',
            borderRadius: '16px',
            padding: '20px 28px',
            display: 'flex',
            flexDirection: 'column',
            gap: '16px',
            boxShadow: '0 8px 24px -6px rgba(2, 132, 199, 0.25)',
            position: 'relative',
            overflow: 'hidden',
          }}>
            <div style={{ position: 'absolute', top: '-30px', right: '80px', width: '130px', height: '130px', borderRadius: '50%', background: 'rgba(255,255,255,0.04)', pointerEvents: 'none' }} />
            <div style={{ position: 'absolute', bottom: '-50px', right: '30px', width: '160px', height: '160px', borderRadius: '50%', background: 'rgba(255,255,255,0.03)', pointerEvents: 'none' }} />

            {/* Top row: Title + Features */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '12px' }}>
              <div>
                <h2 style={{ color: '#ffffff', fontSize: '20px', fontWeight: '800', margin: '0 0 4px', letterSpacing: '-0.3px' }}>
                  Add Products to Your Store
                </h2>
                <p style={{ color: '#94a3b8', fontSize: '13px', margin: 0 }}>
                  Choose how you'd like to add products — bulk upload or fill manually.
                </p>
              </div>
              <div style={{ display: 'flex', gap: '20px', flexWrap: 'wrap' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <FiZap size={14} color="#fbbf24" />
                  <span style={{ color: '#cbd5e1', fontSize: '12px', fontWeight: '500', whiteSpace: 'nowrap' }}>Bulk import 1000+</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <FiLock size={14} color="#fbbf24" />
                  <span style={{ color: '#cbd5e1', fontSize: '12px', fontWeight: '500', whiteSpace: 'nowrap' }}>SKU-locked template</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <FiCheckCircle size={14} color="#34d399" />
                  <span style={{ color: '#cbd5e1', fontSize: '12px', fontWeight: '500', whiteSpace: 'nowrap' }}>Error reports</span>
                </div>
              </div>
            </div>

            {/* Download Strip (white bg inside hero) */}
            <div style={{
              background: '#ffffff', borderRadius: '12px',
              padding: '10px 18px', display: 'flex', alignItems: 'center',
              justifyContent: 'space-between', flexWrap: 'wrap', gap: '10px',
              boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <FiAlertCircle size={18} color="#d97706" style={{ flexShrink: 0 }} />
                <span style={{ fontSize: '13px', color: '#334155', fontWeight: '600' }}>
                  Download template first →&nbsp;
                  <span style={{ fontWeight: '500', color: '#64748b' }}>Fill: MRP · Discount % (Selling Price auto-calculates) · GST % · Stock · Warranty · Replacement Days</span>
                </span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                <button
                  onClick={() => downloadTemplate('xlsx')}
                  style={{
                    display: 'flex', alignItems: 'center', gap: '7px',
                    padding: '8px 16px', borderRadius: '9px', border: 'none',
                    background: 'linear-gradient(135deg, #10b981, #059669)',
                    color: '#fff', fontWeight: '700', fontSize: '12.5px',
                    cursor: 'pointer', boxShadow: '0 3px 10px rgba(16, 185, 129, 0.3)',
                    transition: 'all 0.2s', whiteSpace: 'nowrap',
                  }}
                  onMouseOver={(e) => { e.currentTarget.style.transform = 'translateY(-1px)'; }}
                  onMouseOut={(e) => { e.currentTarget.style.transform = 'translateY(0)'; }}
                >
                  <BsFileSpreadsheet size={15} /> Download All Products
                </button>
                <button
                  onClick={() => setIsTemplateModalOpen(true)}
                  style={{
                    display: 'flex', alignItems: 'center', gap: '7px',
                    padding: '8px 16px', borderRadius: '9px', border: 'none',
                    background: 'linear-gradient(135deg, #f59e0b, #d97706)',
                    color: '#fff', fontWeight: '700', fontSize: '12.5px',
                    cursor: 'pointer', boxShadow: '0 3px 10px rgba(217, 119, 6, 0.3)',
                    transition: 'all 0.2s', whiteSpace: 'nowrap',
                  }}
                  onMouseOver={(e) => { e.currentTarget.style.transform = 'translateY(-1px)'; }}
                  onMouseOut={(e) => { e.currentTarget.style.transform = 'translateY(0)'; }}
                >
                  <BsGrid3X3Gap size={15} /> Select Custom Products
                </button>
              </div>
            </div>
          </div>


          {/* ── Method Cards (horizontal side-by-side) ── */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '16px' , height:"30vh"}}>

            {/* CSV / Excel Card */}
            <div
              onClick={() => setMode('csv')}
              style={{
                background: '#ffffff', borderRadius: '16px', border: '2px solid #e0f2fe',
                padding: '20px 24px', cursor: 'pointer', transition: 'all 0.22s ease',
                boxShadow: '0 2px 10px rgba(2, 132, 199, 0.07)',
                display: 'flex', alignItems: 'center', gap: '18px', position: 'relative',
              }}
              onMouseEnter={(e) => { const el = e.currentTarget; el.style.borderColor = '#0284c7'; el.style.boxShadow = '0 8px 24px rgba(2, 132, 199, 0.15)'; el.style.transform = 'translateY(-3px)'; }}
              onMouseLeave={(e) => { const el = e.currentTarget; el.style.borderColor = '#e0f2fe'; el.style.boxShadow = '0 2px 10px rgba(2, 132, 199, 0.07)'; el.style.transform = 'translateY(0)'; }}
            >
              <div style={{ position: 'absolute', top: '14px', right: '14px', background: 'linear-gradient(135deg, #0284c7, #0ea5e9)', color: '#fff', fontSize: '10px', fontWeight: '700', padding: '2px 8px', borderRadius: '20px' }}>
                RECOMMENDED
              </div>
              <div style={{ width: '52px', height: '52px', borderRadius: '14px', background: 'linear-gradient(135deg, #e0f2fe, #bae6fd)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, boxShadow: '0 4px 10px rgba(2,132,199,0.15)' }}>
                <BsFileSpreadsheet size={26} color="#0284c7" />
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                  <h3 style={{ fontSize: '16px', fontWeight: '800', color: '#0f172a', margin: 0 }}>Bulk Upload (CSV / Excel)</h3>
                </div>
                <p style={{ fontSize: '12.5px', color: '#64748b', margin: '0 0 10px', lineHeight: '1.5' }}>
                  Import many products at once using our Excel template. Perfect for large catalogues.
                </p>
                <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                  {['CSV', 'Excel (.xlsx)', '1000+ rows'].map((tag, i) => (
                    <span key={i} style={{ background: '#e0f2fe', color: '#0369a1', fontSize: '11px', fontWeight: '600', padding: '2px 8px', borderRadius: '20px' }}>{tag}</span>
                  ))}
                </div>
              </div>
              <div style={{ color: '#0284c7', fontSize: '20px', flexShrink: 0 }}>→</div>
            </div>

            {/* Manual Card */}
            <div
              onClick={() => setMode('manual')}
              style={{
                background: '#ffffff', borderRadius: '16px', border: '2px solid #d1fae5',
                padding: '20px 24px', cursor: 'pointer', transition: 'all 0.22s ease',
                boxShadow: '0 2px 10px rgba(5, 150, 105, 0.07)',
                display: 'flex', alignItems: 'center', gap: '18px',
              }}
              onMouseEnter={(e) => { const el = e.currentTarget; el.style.borderColor = '#059669'; el.style.boxShadow = '0 8px 24px rgba(5, 150, 105, 0.15)'; el.style.transform = 'translateY(-3px)'; }}
              onMouseLeave={(e) => { const el = e.currentTarget; el.style.borderColor = '#d1fae5'; el.style.boxShadow = '0 2px 10px rgba(5, 150, 105, 0.07)'; el.style.transform = 'translateY(0)'; }}
            >
              <div style={{ width: '52px', height: '52px', borderRadius: '14px', background: 'linear-gradient(135deg, #d1fae5, #a7f3d0)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, boxShadow: '0 4px 10px rgba(5,150,105,0.15)' }}>
                <BsFillKeyboardFill size={26} color="#059669" />
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <h3 style={{ fontSize: '16px', fontWeight: '800', color: '#0f172a', margin: '0 0 4px' }}>Add Product Manually</h3>
                <p style={{ fontSize: '12.5px', color: '#64748b', margin: '0 0 10px', lineHeight: '1.5' }}>
                  Fill a step-by-step form to add a single product with full control over every detail.
                </p>
                <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                  {['1 product', 'Step-by-step', 'Full control'].map((tag, i) => (
                    <span key={i} style={{ background: '#d1fae5', color: '#065f46', fontSize: '11px', fontWeight: '600', padding: '2px 8px', borderRadius: '20px' }}>{tag}</span>
                  ))}
                </div>
              </div>
              <div style={{ color: '#059669', fontSize: '20px', flexShrink: 0 }}>→</div>
            </div>
          </div>

          {/* ── Compact steps bar ── */}
          <div style={{ background: '#f8fafc', borderRadius: '12px', padding: '14px 20px', border: '1px solid #e2e8f0', display: 'flex', alignItems: 'flex-start', gap: '8px', flexWrap: 'wrap' }}>
            <span style={{ fontSize: '12px', fontWeight: '700', color: '#475569', whiteSpace: 'nowrap', paddingTop: '1px' }}>📋 Bulk Upload Steps:</span>
            {[
              { step: '1', color: '#0284c7', bg: '#dbeafe', title: 'Download Template' },
              { step: '2', color: '#7c3aed', bg: '#ede9fe', title: 'Fill Yellow Cells' },
              { step: '3', color: '#059669', bg: '#d1fae5', title: 'Upload File' },
              { step: '4', color: '#d97706', bg: '#fef3c7', title: 'Review Results' },
            ].map((item, idx, arr) => (
              <div key={item.step} style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <div style={{ width: '22px', height: '22px', borderRadius: '7px', background: item.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: '800', color: item.color, fontSize: '12px', flexShrink: 0 }}>{item.step}</div>
                <span style={{ fontSize: '12px', fontWeight: '600', color: '#334155', whiteSpace: 'nowrap' }}>{item.title}</span>
                {idx < arr.length - 1 && <span style={{ color: '#cbd5e1', fontSize: '14px', margin: '0 2px' }}>›</span>}
              </div>
            ))}
          </div>

        </div>
      )}

      {/* CSV panel */}
      {mode === 'csv' && <CSVUploadPanel />}

      {/* Manual panel */}
      {mode === 'manual' && <ManualAddPanel onCancel={() => setMode('choose')} />}

      <TemplateSelectionModal isOpen={isTemplateModalOpen} onClose={() => setIsTemplateModalOpen(false)} />
    </div>
  );
}

